import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PlayCircle, Download, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getNote } from "@/lib/noteClaim";
import { B, AMBER, GREEN, FAINT, GREY2 } from "./thModel";

// Sign + claim state read from the note in Encounter review / Claims Queue (note_id on the visit).
export function useVisitNote(v) {
  return useQuery({ queryKey: ["th-act-note", v.note_id], enabled: !!v.note_id, staleTime: 30_000, queryFn: () => getNote(v.note_id) });
}

const Chip = ({ ok, children, testid }) => (
  <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2 py-0.5 rounded-full" style={ok ? { background: "#E7F7EC", color: GREEN } : { background: "#FFF4E0", color: AMBER }} data-testid={testid}>{children}</span>
);

export function NoteStatus({ v, note }) {
  const id = v.visit_id;
  if (!v.note_id) return <span className="text-[11.5px]" style={{ color: FAINT }} data-testid={`th-act-nonote-${id}`}>Not saved to Encounter review yet</span>;
  if (note.isLoading) return <span className="text-[11.5px]" style={{ color: FAINT }}>Checking sign and claim…</span>;
  const n = note.data;
  if (!n) return <span className="text-[11.5px]" style={{ color: FAINT }}>Couldn't read the encounter</span>;
  const claimed = n.claim_status === "queued";
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5" data-testid={`th-act-status-${id}`}>
      <Chip ok={n.signed === true} testid={`th-act-sign-${id}`}>{n.signed === true ? "Signed" : "Not signed"}</Chip>
      <Chip ok={claimed} testid={`th-act-claim-${id}`}>{claimed ? "Claim generated" : "Claim pending"}</Chip>
      {n.visit_id ? <span className="font-mono text-[11px]" style={{ color: GREY2 }}>{n.visit_id}</span> : null}
    </span>
  );
}

// Recording link from GET /televisit/{visit_id}/recording (signed URL, ~1 h) — fetched on click.
export function useRecording(v) {
  const [url, setUrl] = useState(null), [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get(`${B}/${v.visit_id}/recording`); const u = data?.download_link || data?.url || data?.recording_url; if (u) setUrl(u); else toast("No recording link yet."); }
    catch (e) { const s = e?.response?.status; toast[s === 404 ? "message" : "error"](s === 404 ? "There's no recording for this visit yet. It appears a few minutes after the call ends." : "Couldn't load the recording."); }
    finally { setBusy(false); }
  };
  return { url, busy, load, close: () => setUrl(null) };
}

export function RecordingButton({ v, rec }) {
  // Recording access is shared: any visit past the live call can ask the API for its link.
  const pre = ["scheduled", "created", "waiting", "live", "in_progress", "started", "cancelled", "no_show"].includes(v.status);
  if (!v.recording_id && (pre || !v.status)) return null;
  return <button type="button" className="th-btn sec" onClick={rec.url ? rec.close : rec.load} disabled={rec.busy} data-testid={`th-act-rec-${v.visit_id}`}><PlayCircle size={13} />{rec.busy ? "Loading…" : rec.url ? "Hide recording" : "Recording"}</button>;
}

export function RecordingPlayer({ v, rec }) {
  if (!rec.url) return null;
  return (
    <div className="mx-4 mb-3 rounded-[10px] p-3 flex flex-col gap-2" style={{ background: "#F7F7F9" }} data-testid={`th-act-player-${v.visit_id}`}>
      <video src={rec.url} controls className="w-full max-w-[560px] rounded-[8px]" style={{ background: "#000" }} data-testid={`th-act-video-${v.visit_id}`} />
      <div className="flex items-center gap-3 text-[12px]">
        <a href={rec.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold" style={{ color: "#4A4AC2" }} data-testid={`th-act-rec-dl-${v.visit_id}`}><Download size={13} />Download</a>
        <span style={{ color: FAINT }}>The link works for about an hour.</span>
        <button type="button" className="ml-auto th-ibtn" aria-label="Close recording" onClick={rec.close} data-testid={`th-act-rec-close-${v.visit_id}`}><X size={14} /></button>
      </div>
    </div>
  );
}
